import React, { useState, useMemo } from 'react';
import styles from '../../styles/EraTable.module.css';
import InfoTooltip from './InfoTooltip';
import { getEraData, getOverlapYearsData, formatEraYear } from '../../utils/eraCalculations';

/**
 * テーブルデータを生成するカスタムフック
 */
function useEraTableData() {
  // データ生成のメモ化
  return useMemo(() => {
    const currentYear = new Date().getFullYear();
    const futureStart = currentYear + 10; // 今年から10年後をスタート
    const minYear = 1868; // 明治元年
    
    // 各元号の情報
    const eras = getEraData(futureStart);
    // 重複年の特別処理
    const overlapYears = getOverlapYearsData();
    
    // 10年グループの数を計算
    const numGroups = Math.ceil((futureStart - minYear + 1) / 10);
    
    // 各グループの開始年を計算
    const groupStartYears = [];
    for (let i = 0; i < numGroups; i++) {
      let startYear;
      if (i === 0) {
        startYear = futureStart; // 最初のグループは今年の10年後から
      } else if (i === 1) {
        startYear = currentYear; // 2つ目のグループは今年から
      } else {
        startYear = groupStartYears[i-1] - 10; // 前のグループの10年前から
      }
      
      groupStartYears.push(startYear);
    }
    
    // 各グループの年データを作成
    const groupsData = [];
    
    // 各グループで処理
    for (let groupIndex = 0; groupIndex < numGroups; groupIndex++) {
      const startYear = groupStartYears[groupIndex];
      const years = [];
      
      // 各グループ内の年を処理（最大10年）
      for (let yearOffset = 0; yearOffset < 10; yearOffset++) {
        const westernYear = startYear - yearOffset;
        
        // 明治元年より前は対象外
        if (westernYear < minYear) {
          years.push(null);
          continue;
        }
        
        // 元号切り替わり年の特別処理
        if (overlapYears[westernYear]) {
          const overlapData = overlapYears[westernYear];
          const eraEntries = Object.entries(overlapData);
          
          years.push({
            westernYear,
            isCurrentYear: westernYear === currentYear,
            isOverlapYear: true,
            bothEras: eraEntries.map(([era, year]) => `${era}${formatEraYear(year)}`)
          });
          continue;
        }
        
        // 通常の年の処理
        for (const era of eras) {
          if (westernYear >= era.start && westernYear < era.end) {
            years.push({
              westernYear,
              eraName: era.name,
              eraYear: formatEraYear(westernYear - era.offset),
              isCurrentYear: westernYear === currentYear,
              isOverlapYear: false
            });
            break;
          }
        }
      }
      
      groupsData.push(years);
    }
    
    return {
      tableData: groupsData,
      // テーブルの行数を求める（各グループの最大長さ）
      maxRowCount: Math.max(...groupsData.map(group => group.length))
    };
  }, []);
}

/**
 * 年データを変換する関数
 */
function convertYearDataForSelection(yearData) {
  if (!yearData) return null;
  
  if (yearData.isOverlapYear && yearData.bothEras) {
    // 重複年のデータを変換して渡す
    const overlapYears = getOverlapYearsData();
    const eraData = overlapYears[yearData.westernYear];
    const eraEntries = Object.entries(eraData);
    
    return {
      westernYear: yearData.westernYear,
      primaryEra: eraEntries[0][0], // 新しい元号
      primaryEraYear: eraEntries[0][1],
      secondaryEra: eraEntries[1][0], // 古い元号
      secondaryEraYear: eraEntries[1][1],
      isOverlapYear: true
    };
  }
  
  // 通常の年
  return {
    westernYear: yearData.westernYear,
    primaryEra: yearData.eraName,
    primaryEraYear: yearData.eraYear.toString() === '元' ? 1 : parseInt(yearData.eraYear),
    isOverlapYear: false
  };
}

/**
 * 早見表メインコンポーネント
 */
const EraTable = ({ onYearSelect }) => {
  const { tableData, maxRowCount } = useEraTableData();
  
  // 年をクリックしたときの処理
  const handleYearClick = (yearData) => {
    if (!onYearSelect || !yearData) return;
    
    const selectionData = convertYearDataForSelection(yearData);
    onYearSelect(selectionData);
  };
  
  // テーブルのレンダリング
  const renderYearCell = (yearData, groupIndex, isLastGroup) => {
    if (!yearData) {
      return (
        <td className={styles.yearPair}>
          {!isLastGroup && <div className={styles.groupBorder}></div>}
        </td>
      );
    }
    
    return (
      <td 
        className={`${styles.yearPair} ${yearData.isCurrentYear ? styles.currentYear : ''}`}
        onClick={() => handleYearClick(yearData)}
      >
        <div className={styles.yearPairInner}>
          <span className={`${styles.eraYear} ${yearData.isOverlapYear ? styles.overlapYear : ''}`}>
            {yearData.bothEras ? (
              <div className={styles.dualEraDisplay}>
                {yearData.bothEras.map((era, i) => (
                  <div key={i}>{era}</div>
                ))}
              </div>
            ) : (
              `${yearData.eraName}${yearData.eraYear}`
            )}
          </span>
          <span className={styles.westernYear}>
            {yearData.westernYear}
          </span>
        </div>
        {!isLastGroup && <div className={styles.groupBorder}></div>}
      </td>
    );
  };
  
  return (
    <div className={styles.eraTableContainer}>
      <div className={styles.eraTable}>
        <h4 className={styles.tableHeader}>
          和暦・西暦早見表
          <InfoTooltip />
        </h4>
        <div className={styles.continuousYearsWrapper}>
          <table className={styles.continuousEraTable}>
            <tbody>
              {Array.from({ length: maxRowCount }).map((_, rowIndex) => (
                <tr key={rowIndex}>
                  {tableData.map((group, groupIndex) => (
                    <React.Fragment key={`group-${groupIndex}-row-${rowIndex}`}>
                      {renderYearCell(
                        group[rowIndex], 
                        groupIndex, 
                        groupIndex === tableData.length - 1
                      )}
                    </React.Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EraTable; 